import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import { createContext, useContext, type ReactNode } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import type { NovaEmbedContext } from '../../../lib/embed-context';
import type { UIMessageWithMetadata } from '../../../lib/types';
import type { ChatAppType } from '../../../queries/chats';
import type { SelectedAccount } from '../../account-selector';
import type { SettingsAdministration } from '../components/skill-administration/types';
import type { ChatNotificationController } from '../hooks/use-chat-notification-controller';
import type { NovaChatBackend } from '../lib/chat-backend';
import type { ChatError, RetryLastChatRequest } from '../lib/shared-types';
import type { NovaNavigationActions } from '../navigation/types';
import type { NovaProviderCallbacks } from '../providers/nova-provider';

export interface StartNewChatWithInputOptions {
  additionalContext?: Record<string, unknown>;
  submitImmediately?: boolean;
  /** The new chat's uuid, when files were already uploaded into its workspace; a fresh one otherwise. */
  chatUuid?: string;
}

export interface QueuedNewChatInput extends StartNewChatWithInputOptions {
  inputText: string;
}

export type ChatSettingsSection = 'general' | 'scheduled' | 'skills' | 'teams' | 'admins' | 'analytics';

export interface ChatMessageNavigationTarget {
  chatUuid: string;
  messageUuid: string;
}

export interface ChatOverlayContextValue {
  // Sidebar state
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;

  // Fullscreen state
  isFullscreen: boolean;
  setIsFullscreen: (fullscreen: boolean) => void;

  // Account context
  accountUuid?: string;
  accountName?: string;
  accountProgramType?: string;
  selectedAccount?: SelectedAccount;
  setAccountUuid: (uuid?: string) => void;
  onSelectAccount: (account: SelectedAccount) => Promise<void>;
  canSelectAccount: boolean;
  canConnectOutlook: boolean;
  isWtwColleague: boolean;

  // Chat state
  chatUuid: string;
  isNewChat: boolean;
  isReadOnly: boolean;
  hasAnyMessages: boolean;
  setHasAnyMessages: (hasMessages: boolean) => void;
  startNewChat: () => void;
  startNewChatWithInput: (inputText: string, options?: StartNewChatWithInputOptions) => void;
  setActiveChat: (chatUuid: string, accountUuid?: string | null) => void;
  messageNavigationTarget?: ChatMessageNavigationTarget;
  openChatAtMessage: (chatUuid: string, messageUuid: string) => void;
  clearMessageNavigationTarget: (target: ChatMessageNavigationTarget) => void;

  // View mode
  isStandaloneMode: boolean;
  isEmbedded: boolean;
  /** The embedding host's context for the current chat: any new chat, or the chat it was sent with. */
  embedContext?: NovaEmbedContext;
  /** Records that the chat's first message carried the embed context, so it stays with that chat. */
  onEmbedContextSent?: (chatUuid: string, embedContext: NovaEmbedContext) => void;
  isVisible: boolean;

  // User info (needed for AccountSelector and NovaChatProvider)
  userUuid: string;
  userScopes?: string[];
  authProvider: Provider<AuthProviderContext>;

  // The backend this chat sends turns to
  chatBackend: NovaChatBackend;

  // App context (used for filtering chats by origin, e.g. OUTLOOK)
  appType?: ChatAppType;

  // App type chat lists and their Apollo cache entries filter by; null lists chats from every app
  effectiveAppType: ChatAppType | null | undefined;

  // Current chat's origin app type (e.g., 'OUTLOOK' for chats started in Outlook)
  currentChatAppType?: ChatAppType;

  // Close handler
  onClose?: () => void;

  // Display account (from chat or context)
  displayAccount?: {
    uuid: string;
    name: string;
  };

  // Query account UUID from URL
  queryAccountUuid?: string | undefined;
  pageAccountUuid?: string | undefined;

  // Chat input placeholder
  placeholder: string;

  // Messages loading state
  initialMessages: UIMessageWithMetadata[];
  isLoadingMessages: boolean;
  /** The routed chat does not exist for this user on this server. */
  isChatMissing?: boolean;
  canResumeActiveStream: boolean;
  chatHasActiveStream: boolean;
  chatActiveStreamId?: string | null;
  pendingRegenerationMessage?: UIMessageWithMetadata;
  isActiveChat: (chatUuid: string) => boolean;
  beginRegeneration: (chatUuid: string, message: UIMessageWithMetadata) => void;
  clearPendingRegeneration: (chatUuid: string) => void;
  refetchMessages: (chatUuid: string) => Promise<UIMessageWithMetadata[] | undefined>;

  // Error state
  chatError?: ChatError;
  setChatError: (error?: ChatError) => void;
  retryLastRequest?: RetryLastChatRequest;
  isRetryingLastRequest: boolean;
  setRetryLastRequest: (retry?: RetryLastChatRequest) => void;

  // Notifications state
  hasNotifications: boolean;

  // Additional context for API requests (set by toolbar components like ChatAttachments)
  // Components can update this via setAdditionalContext to add data to the API request
  additionalContext?: Record<string, unknown>;
  setAdditionalContext: (
    context: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>),
  ) => void;
  queuedNewChatInput?: QueuedNewChatInput | null;
  clearQueuedNewChatInput: () => void;

  // Callbacks
  callbacks?: NovaProviderCallbacks;

  // Browser notification controls and status
  notificationController: ChatNotificationController;

  /** Available on interactive NovaProvider surfaces. Snapshot providers omit navigation. */
  navigationActions?: NovaNavigationActions;

  // Settings panel state
  settingsAdministration: SettingsAdministration;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (open: boolean) => void;
  settingsSection: ChatSettingsSection;
  setSettingsSection: (section: ChatSettingsSection) => void;
  /** Go back to main settings view (dirty check if needed) */
  requestSettingsBack: () => void;
  /** Close settings entirely after dirty check (for navigation actions like New Chat) */
  requestSettingsClose: (onClose?: () => void) => void;
  /** Whether the settings panel has an active sub-view (e.g. Outlook Manage) */
  hasActiveSettingsSubView: boolean;

  // Public mode (e.g. Benji) — skips internal GraphQL queries and admin-only features
  isPublicMode?: boolean;

  // Custom error message shown when a stream request fails
  errorMessage?: string;

  // Outlook Connect state
  hasSavedOutlookConnection: boolean;
  outlookConnectEnabled: boolean;
  outlookNeedsReauth: boolean;
  reconnectOutlook: () => Promise<boolean>;
  setOutlookConnectEnabled: (enabled: boolean) => void;
  disconnectOutlook: () => Promise<void>;

  // Persisted nova settings from server
  serverSettings: NovaSettingsData | null;
  persistSetting: (field: NovaSettingField, value: unknown) => void;
  persistSettings: (fields: Partial<NovaSettingsData>) => void;
}

const ChatOverlayContext = createContext<ChatOverlayContextValue | undefined>(undefined);

interface ChatOverlayProviderProps {
  children: ReactNode;
  value: ChatOverlayContextValue;
}

/**
 * Provides chat overlay state (sidebar, account, fullscreen, etc.)
 * to child components without prop drilling.
 */
export function ChatOverlayProvider({ children, value }: ChatOverlayProviderProps) {
  return <ChatOverlayContext.Provider value={value}>{children}</ChatOverlayContext.Provider>;
}

/**
 * Hook to access chat overlay context.
 * @throws Error if used outside of ChatOverlayProvider
 */
export function useChatOverlayContext(): ChatOverlayContextValue {
  const context = useContext(ChatOverlayContext);
  if (!context) {
    throw new Error('useChatOverlayContext must be used within ChatOverlayProvider');
  }
  return context;
}

/**
 * Safe hook to access chat overlay context.
 * Returns undefined if used outside of ChatOverlayProvider.
 * Use this when the provider might not be available (e.g., when ChatOverlayProviderV2 renders provider in a Portal).
 */
export function useChatOverlayContextSafe(): ChatOverlayContextValue | undefined {
  return useContext(ChatOverlayContext);
}
